/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Client module `BreakfastEventInviteesList` for the Caintra Connect frontend.
 * @module pages/BreakfastEventInviteesList
 *
 * Conventions:
 * - Keep components focused on rendering; move branching logic into hooks or utils.
 * - Prefer named exports for helpers and default exports for React components.
 * - Reuse shared validation, i18n, and API helpers from client/src/utils and logic/.
 */
import { logger, sanitizeError } from "../utils/logger.js";
import styles from "../styles/BreakfastEventInviteesList.module.css";

import { AppContext } from "../context/AppContext.js";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import SearchBar from "../components/SearchBar/index.jsx";
import ResultsDropdown from "../components/ResultsDropdown/index.jsx";
import PageSelector from "../components/PageSelector/index.jsx";
import BreakfastEventCompaniesTable, {
  rowId,
} from "../components/BreakfastEventCompaniesTable/index.jsx";

import { CircularProgress, Backdrop } from "@mui/material";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import { use, useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import {
  getBreakfastEventDetails,
  getBreakfastEventParticipatingCompaniesByStatus,
  fetchMoreParticipatingCompaniesByStatus,
  confirmBreakfastEventRegistrations,
  rejectBreakfastEventParticipatingCompany,
} from "../logic/breakfastEvents";
import { getUser } from "../logic/user";
import { canManageBreakfastEvents } from "../utils/accessControl";
import useRequireAccess from "../hooks/useRequireAccess";

import { ReactComponent as TitleIcon } from "../assets/icons/BusinessEvents/TitleIcon.svg";
import { ReactComponent as CheckCircleIcon } from "../assets/icons/BusinessEvents/CheckCircleIcon.svg";
import { DEFAULT_RESULTS_LIMIT } from "../utils/resultsLimitOptions.js";

const BreakfastEventInviteesList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { eventId } = useParams();
  const { changePermissions } = use(AppContext);
  const { allowed } = useRequireAccess(canManageBreakfastEvents);

  const [sortConfig, setSortConfig] = useState({
    key: "nombreComercial",
    direction: "ascending",
  });
  const [accessOk, setAccessOk] = useState(false);
  const [eventData, setEventData] = useState(null);
  const [companiesData, setCompaniesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resultsLimit, setResultsLimit] = useState(DEFAULT_RESULTS_LIMIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [user, setUser] = useState(null);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [isSlidingUp, setIsSlidingUp] = useState(false);
  const [successMessageVariant, setSuccessMessageVariant] = useState(null);
  const [lastKey, setLastKey] = useState(null);
  const [limitReached, setLimitReached] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const isLaptop = window.innerWidth >= 1024 && window.innerWidth <= 1440;
  const isTablet = window.innerWidth <= 1023;
  const isMediumMobile = window.innerWidth <= 767;
  const isSmallMobile = window.innerWidth <= 480;

  const getDevice = () => {
    if (isSmallMobile) {
      return "smallMobile";
    }
    if (isMediumMobile) {
      return "mediumMobile";
    }
    if (isLaptop) {
      return "laptop";
    }
    if (isTablet) {
      return "tablet";
    }
    return "desktop";
  };

  const getSearchBarWidth = (device) => {
    if (device === "laptop") {
      return "80.2%";
    }
    if (device === "tablet") {
      return "73%";
    }
    if (device === "mediumMobile") {
      return "90%";
    }
    if (device === "smallMobile") {
      return "75%";
    }
    return "86%";
  };

  const loadData = useCallback(async () => {
    if (!allowed) {
      return;
    }
    const userId = localStorage.getItem("userId");
    if (!userId || !eventId) {
      navigate("/events", { replace: true });
      return;
    }
    setIsLoading(true);
    try {
      const userData = await getUser(userId, navigate);
      setUser(userData);
      setAccessOk(true);

      await getBreakfastEventDetails(eventId, navigate, dayjs, setEventData);
      const data = await getBreakfastEventParticipatingCompaniesByStatus(
        eventId,
        "INTERESTED",
        navigate,
      );
      setCompaniesData(data.companies);
      setLastKey(data.lastEvaluatedKey ?? null);
      setLimitReached(false);
    } finally {
      setIsLoading(false);
    }
  }, [allowed, eventId, navigate]);

  useEffect(() => {
    loadData().catch((err) => {
      logger.error("Error", { err: sanitizeError(err) });
    });
  }, [loadData]);

  const confirmCompaniesRegistrations = async () => {
    try {
      setIsLoading(true);
      logger.info("selectedIds: ", { data: selectedIds });
      logger.info("eventId: ", { data: eventId });
      logger.info("Array.from(selectedIds): ", {
        data: Array.from(selectedIds),
      });
      await confirmBreakfastEventRegistrations(
        eventId,
        Array.from(selectedIds),
        navigate,
      );
      setShowSuccessMessage(true);
      setIsSlidingUp(true);
      setSuccessMessageVariant("created");
      setCompaniesData(
        companiesData.filter((company) => !selectedIds.has(company.idEmpresa)),
      );
      setSelectedIds(new Set());
    } catch (error) {
      logger.error("Error confirming companies registrations:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRejectInvitation = async (company, index) => {
    setIsLoading(true);
    try {
      const result = await rejectBreakfastEventParticipatingCompany(
        eventId,
        company.idEmpresa,
        navigate,
      );
      if (result == null) {
        return;
      }

      setCompaniesData((prev) =>
        prev.filter((c) => c.idEmpresa !== company.idEmpresa),
      );
      const id = rowId(company, index);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    } catch (error) {
      logger.error("Error rejecting invitation:", {
        err: sanitizeError(error),
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Renders the sort direction arrow icon (rotated when ascending).
   * @returns {JSX.Element} Arrow icon for the subject column header.
   */
  const renderArrow = () => {
    return (
      <ArrowDownwardIcon
        className={`${styles.arrowIcon} ${
          sortConfig.direction === "ascending" ? styles.rotated : ""
        }`}
      />
    );
  };

  // useEffect(() => {
  //   if (location.state?.eventCreated) {
  //     setSuccessMessageVariant("created");
  //     showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
  //     navigate(location.pathname, { replace: true, state: {} });
  //   } else if (location.state?.eventUpdated) {
  //     setSuccessMessageVariant("updated");
  //     showEventCreatedSuccess(setShowSuccessMessage, setIsSlidingUp);
  //     navigate(location.pathname, { replace: true, state: {} });
  //   }
  // }, [location.state, navigate, location.pathname]);

  const handleSearch = (event) => {
    setSearchQuery(event.target.value);
  };

  const handleResultsLimitChange = (selectedOption) => {
    setResultsLimit(selectedOption.value);
    setCurrentPage(1);
  };

  const handleViewProfile = async (company) => {
    try {
      navigate(`/companyProfile`, {
        state: {
          company: company,
        },
      });
    } catch (error) {
      logger.error("Error", { err: sanitizeError(error) });
    }
  };

  const filteredCompaniesData = companiesData?.filter((company) =>
    company.nombreComercial
      ? company.nombreComercial
          .toLowerCase()
          .includes(searchQuery.toLowerCase())
      : false,
  );

  const handlePageChange = async (direction) => {
    const totalPages = Math.ceil(filteredCompaniesData.length / resultsLimit);
    if (direction === "next") {
      if (currentPage < totalPages) {
        setCurrentPage((prevPage) => prevPage + 1);
      } else if (lastKey) {
        try {
          setIsLoading(true);
          const data = await fetchMoreParticipatingCompaniesByStatus(
            eventId,
            "INTERESTED",
            navigate,
            lastKey,
          );
          setCompaniesData((prev) => [...prev, ...data.companies]);
          setLastKey(data.lastEvaluatedKey ?? null);
          setCurrentPage((prevPage) => prevPage + 1);
        } catch (error) {
          logger.error(
            "Error fetching more breakfast event participating companies:",
            { err: sanitizeError(error) },
          );
        } finally {
          setIsLoading(false);
        }
      } else {
        setLimitReached(true);
      }
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
      setLimitReached(false);
    } else {
      // Unsupported direction or already at bounds. No action required.
    }
  };

  const paginatedCompaniesData = filteredCompaniesData.slice(
    (currentPage - 1) * resultsLimit,
    currentPage * resultsLimit,
  );

  if (!allowed || (!accessOk && !isLoading)) {
    return null;
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.languages}>
          <NotificationBell navigate={navigate} />
          <LanguageSelector />
        </div>

        <div className={styles.headerInfoContainer}>
          <div className={styles.headerInfo}>
            <TitleIcon className={styles.titleIcon} />
            <h1 className={styles.title}>
              {t("pages.breakfastEventInviteesList.header")}
            </h1>
          </div>
          <div className={styles.searchSection}>
            <SearchBar
              placeholder={
                isSmallMobile || isMediumMobile
                  ? t("pages.breakfastEventInviteesList.mobileSearchText")
                  : t("pages.breakfastEventInviteesList.searchText")
              }
              value={searchQuery}
              onChange={handleSearch}
              width={getSearchBarWidth(getDevice())}
            />
          </div>
        </div>
      </div>

      <div className={styles.eventsList}>
        {paginatedCompaniesData.length === 0 && (
          <div className={styles.noResults}>
            <p>{t("pages.breakfastEventInviteesList.noResults")}</p>
          </div>
        )}
        {paginatedCompaniesData.length > 0 && (
          <BreakfastEventCompaniesTable
            companies={paginatedCompaniesData}
            selectedIds={selectedIds}
            onSelectedIdsChange={setSelectedIds}
            renderArrow={renderArrow}
            // sortConfig={sortConfig}
            onViewProfile={handleViewProfile}
            onRejectInvitation={handleRejectInvitation}
          />
        )}
      </div>
      <div className={styles.bottomContainer}>
        <div className={styles.bottomContainerLeft}>
          <span className={styles.preRegistro}>
            {t("pages.breakfastEventInviteesList.selectedCompanies", {
              selectedCompanies: selectedIds.size ?? 0,
              totalCompanies: eventData?.cupoMaximo ?? 0,
            })}
          </span>
        </div>
        <div className={styles.bottomContainerRight}>
          <button
            onClick={confirmCompaniesRegistrations}
            className={styles.newItemButton}
            disabled={isLoading || selectedIds.size === 0}
          >
            {t("pages.breakfastEventInviteesList.inviteCompanies")}
          </button>
        </div>
      </div>

      <div className={styles.footer}>
        <ResultsDropdown onResultsLimitChange={handleResultsLimitChange} />
        <PageSelector
          currentPage={currentPage}
          totalPages={Math.ceil(filteredCompaniesData.length / resultsLimit)}
          onPageChange={handlePageChange}
          disabled={{ next: limitReached }}
        />
      </div>

      {showSuccessMessage && (
        <div
          className={`${styles.successMessage} ${
            isSlidingUp ? styles.slideUp : ""
          }`}
        >
          <CheckCircleIcon className={styles.successMessageIcon} />
          {t(
            successMessageVariant === "updated"
              ? "pages.breakfastEventInviteesList.updateMessage"
              : "pages.breakfastEventInviteesList.creationMessage",
          )}
        </div>
      )}

      <Backdrop
        sx={{
          color: "#fff",
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
        }}
        open={isLoading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: "#4eabe7",
          }}
        />
      </Backdrop>
    </div>
  );
};

export default BreakfastEventInviteesList;
